import { Form } from "@unform/web";
import { Filter, FolderSync, ArrowRightLeft, Zap, CalendarCog, Receipt, History, HandCoins, Wrench } from "lucide-react";
import React, { createContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import Breadcrumbs from "~/components/Breadcrumbs";
import FiltersBar from "~/components/FiltersBar";
import PageHeader from "~/components/PageHeader";
import FileInput from "~/components/RegisterForm/FileInput";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import Select from "~/components/RegisterForm/Select";
import { getCurrentPage } from "~/functions";
import api from "~/services/api";
import FormData from "form-data";
import Migration from "./Migration";
import ReceivablesMigration from "./ReceivablesMigration";
import StudentHistoryMigration from "./StudentHistoryMigration";
import PayeesMigration from "./PayeesMigration";
import ConfigDate from "./ConfigDate";
import GlobalSessionRepair from "./GlobalSessionRepair";
import ImportStatus from "./ImportStatus";
import PaymentLinksRefresh from "./PaymentLinksRefresh";

export const InputContext = createContext({});

export default function DataSync() {
  const currentPage = getCurrentPage();
  const generalForm = useRef();
  const [successfullyUpdated, setSuccessfullyUpdated] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [pageData] = useState({});
  const [activeMenu, setActiveMenu] = useState("import");
  const [loading, setLoading] = useState(false);
  const [importRun, setImportRun] = useState({});
  const [jobLoading, setJobLoading] = useState(false);
  const [jobCooldown, setJobCooldown] = useState(false);
  const [overdueFlagJobLoading, setOverdueFlagJobLoading] = useState(false);
  const [overdueFlagJobCooldown, setOverdueFlagJobCooldown] = useState(false);

  function handleGeneralFormSubmit(data) {
    setLoading(true);
    setImportRun({
      fileName: data.import_file?.name,
      importType: data.import,
    });
    const formData = new FormData();
    formData.append("importType", data.import);
    formData.append("file", data.import_file);
    api
      .post("/data-sync/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (response.status === 200) {
          setImportRun((run) => ({ ...run, result: response.data }));
          toast("Data imported!", { autoClose: 1000 });
          setSuccessfullyUpdated(true);
        } else {
          setImportRun((run) => ({ ...run, error: "Unexpected server response" }));
          toast("Data not imported!", { autoClose: 1000 });
          setSuccessfullyUpdated(false);
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setImportRun((run) => ({
          ...run,
          error:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            "Unknown error",
        }));
        console.log(error);
      });
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
  }

  async function handleInactivate() {}

  async function handleRunBeforeDueDateAutopay() {
    if (jobLoading || jobCooldown) return;
    setJobLoading(true);
    try {
      await api.post("/receivables/run-before-due-date-autopay-invoices");
      toast("Job executed successfully!", { type: "success", autoClose: 3000 });
      setJobCooldown(true);
      setTimeout(() => setJobCooldown(false), 30000);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      } else {
        toast("Failed to execute job.", { type: "error", autoClose: 3000 });
      }
    } finally {
      setJobLoading(false);
    }
  }

  async function handleRunUpdateOverdueInvoiceFlag() {
    if (overdueFlagJobLoading || overdueFlagJobCooldown) return;
    setOverdueFlagJobLoading(true);
    try {
      await api.post("/students/update-overdue-invoice-flag");
      toast("Job executed successfully!", { type: "success", autoClose: 3000 });
      setOverdueFlagJobCooldown(true);
      setTimeout(() => setOverdueFlagJobCooldown(false), 30000);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      } else {
        toast("Failed to execute job.", { type: "error", autoClose: 3000 });
      }
    } finally {
      setOverdueFlagJobLoading(false);
    }
  }

  return (
    <div className="h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-hidden">
      <PageHeader>
        <Breadcrumbs currentPage={currentPage} />
        <FiltersBar>
          <Filter size={14} /> Custom Filters
        </FiltersBar>
      </PageHeader>

      <div className="flex flex-1 w-full flex-row items-start justify-start gap-4 overflow-hidden">
        <div className="flex flex-col items-start justify-start text-xs w-36 gap-2 pt-4 shrink-0">
          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="import"
          >
            <FolderSync size={16} /> Import
          </RegisterFormMenu>
          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="migration"
          >
            <ArrowRightLeft size={16} /> Migration
          </RegisterFormMenu>
          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="receivables-migration"
          >
            <Receipt size={16} /> Receivables Migration
          </RegisterFormMenu>
          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="student-history-migration"
          >
            <History size={16} /> Student History Migration
          </RegisterFormMenu>
          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="payees-migration"
          >
            <HandCoins size={16} /> Payees Migration
          </RegisterFormMenu>
          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="activate-jobs"
          >
            <Zap size={16} /> Activate Jobs
          </RegisterFormMenu>
          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="global-session-repair"
          >
            <Wrench size={16} /> Global Sessions
          </RegisterFormMenu>
          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="config-date"
          >
            <CalendarCog size={16} /> Config Date
          </RegisterFormMenu>
        </div>

        <div className="flex flex-1 flex-col h-full overflow-hidden pt-4">
          {activeMenu === "import" && (
            <Form
              ref={generalForm}
              onSubmit={handleGeneralFormSubmit}
              encType="multipart/form-data"
              className="w-full min-h-0 overflow-auto"
            >
              <InputContext.Provider
                value={{
                  id: "new",
                  generalForm,
                  setSuccessfullyUpdated,
                  fullscreen,
                  setFullscreen,
                  successfullyUpdated,
                  handleCloseForm,
                  handleInactivate,
                  canceled: pageData.canceled_at,
                }}
              >
                <InputLineGroup title="Import" activeMenu={true}>
                  <h2 className="text-xl font-bold">Import Data (Only CSV files)</h2>
                  <InputLine>
                    <Select
                      name="import"
                      options={[
                        { label: "Students", value: "Students" },
                        {
                          label: "Medical Excuses & Vacations",
                          value: "Medical Excuses & Vacations",
                        },
                        {
                          label: "Gravity Transactions",
                          value: "EmergepayTransactions",
                        },
                      ]}
                      InputContext={InputContext}
                    />
                    <FileInput
                      grow
                      accept=".csv"
                      name="import_file"
                      InputContext={InputContext}
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="text-md font-bold bg-secondary border hover:border-primary hover:text-primary rounded-md p-4 h-6 flex flex-row items-center justify-center text-xs gap-1"
                    >
                      {loading ? "Loading..." : "Import file"}
                    </button>
                  </InputLine>
                  <div className="w-full">
                    <ImportStatus
                      loading={loading}
                      fileName={importRun.fileName}
                      importType={importRun.importType}
                      result={importRun.result}
                      error={importRun.error}
                    />
                  </div>
                </InputLineGroup>
              </InputContext.Provider>
            </Form>
          )}

          {activeMenu === "migration" && <Migration />}

          {activeMenu === "receivables-migration" && <ReceivablesMigration />}

          {activeMenu === "student-history-migration" && <StudentHistoryMigration />}

          {activeMenu === "payees-migration" && <PayeesMigration />}

          {activeMenu === "config-date" && <ConfigDate />}

          {activeMenu === "global-session-repair" && <GlobalSessionRepair />}

          {activeMenu === "activate-jobs" && (
            <InputLineGroup title="Activate Jobs" activeMenu={true}>
              <div className="flex flex-col gap-6 py-2">
                <div className="flex flex-col gap-2">
                  <h2 className="text-xl font-bold">Activate Jobs</h2>
                </div>
                <div className="flex flex-col gap-2 border rounded-lg p-4 max-w-md">
                  <p className="text-sm font-semibold">Before Due Date — Auto Pay Invoices</p>
                  <p className="text-xs text-gray-500">Sends auto pay invoices that are before their due date.</p>
                  <button
                    type="button"
                    disabled={jobLoading || jobCooldown}
                    onClick={handleRunBeforeDueDateAutopay}
                    className="mt-1 text-md font-bold bg-secondary border hover:border-primary hover:text-primary rounded-md p-4 h-8 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Zap size={13} />
                    {jobLoading ? "Running..." : jobCooldown ? "Please wait 30s..." : "Activate Before due date for auto pays"}
                  </button>
                </div>
                <div className="flex flex-col gap-2 border rounded-lg p-4 max-w-md">
                  <p className="text-sm font-semibold">Update Overdue Invoice Flag</p>
                  <p className="text-xs text-gray-500">Updates the student's flag if they have pending overdue invoices.</p>
                  <button
                    type="button"
                    disabled={overdueFlagJobLoading || overdueFlagJobCooldown}
                    onClick={handleRunUpdateOverdueInvoiceFlag}
                    className="mt-1 text-md font-bold bg-secondary border hover:border-primary hover:text-primary rounded-md p-4 h-8 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Zap size={13} />
                    {overdueFlagJobLoading ? "Running..." : overdueFlagJobCooldown ? "Please wait 30s..." : "Activate Update Overdue Invoice Flag"}
                  </button>
                </div>
                <PaymentLinksRefresh />
              </div>
            </InputLineGroup>
          )}
        </div>
      </div>
    </div>
  );
}
